/**
 * Integrate with Sentry.io
 *
 * This should be the first import of `index.ts` thus the `Aaa` prefix.
 */
import * as Sentry from '@sentry/browser';
import packageJson from '../package.json';
import { HekateExternalUtil } from '@onshape/www-inertia';
import type { ErrorTag, HekateFormSubmit } from '@onshape/www-inertia';

const isWistiaError = (event: Sentry.Event) =>
  event.exception?.values?.find((val) =>
    val.stacktrace?.frames?.find((frame) => frame.abs_path?.includes('wistia.com'))
  );

if (!import.meta.server) {
  const env = HekateExternalUtil.isProduction() ? 'production' : 'dev';
  Sentry.init({
    release: `www-onshape@${packageJson.version}`,
    dsn: 'https://383d88fdab7b4a129156980bde0511cb@o523154.ingest.sentry.io/5635145',
    integrations: [],
    denyUrls: [/wistia\.com/i, /engagio\.com/i, /cookielaw\.org/i, /taboola\.com/i],
    tracesSampleRate: 0.005,
    environment: env,
    ignoreErrors: ['E-v1'],
    beforeSend(event: Sentry.ErrorEvent, hint: Sentry.EventHint) {
      if (isWistiaError(event)) {
        return null;
      }

      if (!hint || !hint.originalException) {
        return event;
      }
      const error = hint.originalException;

      let errorMessage = '';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      if (errorMessage.includes('timeout') || errorMessage.includes('error')) {
        return null;
      }

      if (
        errorMessage &&
        errorMessage.match('TypeError: Failed to fetch') &&
        Math.random() > 0.01
      ) {
        return null;
      }

      if (
        errorMessage &&
        errorMessage.match(
          /Object Not Found Matching Id:[0-9]|Extension context invalidated|popoverBorderWidth|Cannot read property '__k' of undefined|Unexpected token '<'|Got an unsuccessful status code: 0/gi
        ) &&
        Math.random() > 0.001
      ) {
        return null;
      }

      return event;
    },
    attachStacktrace: true,
  });
} else {
  console.log('Sentry not initialized on server side');
}

/** Wrapper class for Sentry.io
 *
 * If you change the shape of the methods here, be sure to change them in hekate-www/shims-vue.d.ts
 */
class ErrorReporter {
  private hekateSubmitId: string = '';

  constructor() {
    this.setHekateSubmitIdListener();
  }

  public postToSentry = ({ tag, msg, error }: { tag: ErrorTag; msg: string; error?: Error }) => {
    console.error(`Posting to sentry - msg:${msg} www-tag:${tag}`);

    if (!error) {
      Sentry.withScope((scope) => {
        scope.setFingerprint([tag, msg]);
        scope.setTag('www-tag', tag);
        if (this.hekateSubmitId) scope.setTag('hekate-submit-id', this.hekateSubmitId);
        scope.setLevel('log');
        Sentry.captureMessage(`${tag} - ${msg}`);
      });
      return;
    }
    setTimeout(() => {
      Sentry.withScope((scope) => {
        scope.setFingerprint([tag, msg]);
        scope.setTag('www-tag', tag);
        if (this.hekateSubmitId) scope.setTag('hekate-submit-id', this.hekateSubmitId);
        scope.setLevel('error');
        Sentry.captureException({ tag, msg, error });
      });
    }, 1500);
  };

  private async setHekateSubmitIdListener() {
    if (!window || !window.hekateEventBus) return; // set timeout and try again later?
    window.hekateEventBus.addEventListener(
      'form-successfully-submitted',
      (evt: HekateEventBusCallbackParams<{ formDefinition: HekateFormSubmit }>) => {
        this.hekateSubmitId = evt.data.formDefinition.id;
      }
    );
  }
}

const errorReporter = new ErrorReporter();

if (!import.meta.server) {
  window.errorReporter = errorReporter;
}

export default errorReporter;
